import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TrendingUp, Search, MapPin, AlertCircle, ChevronDown, Calendar, Building, X, Filter } from "lucide-react";
import { ChantierIcon } from "@/components/chantiers/ChantierIcon";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { FiltreRentabilite, FILTRES_CONFIG, FILTRES_LIST } from "@/constants/filtresRentabilite";
import {
  useChantiersFiltresRentabilite,
  useChantiersFiltres,
  useCompteursFiltres,
} from "@/hooks/useChantiersFiltresRentabilite";
import { KPIsSection, KpiSortType } from "@/components/rentabilite/KPIsSection";
import { GraphiquesPanel } from "@/components/rentabilite/GraphiquesPanel";
import { RentabiliteExportButton } from "@/components/rentabilite/RentabiliteExportButton";
import { STATUTS_CONFIG } from "@/constants/statuts";

const Rentabilite = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtreActif, setFiltreActif] = useState<FiltreRentabilite>("engage");
  // Filtre URL marge négative
  const [filterMargeNegative, setFilterMargeNegative] = useState(false);
  const [isUrlFilterActive, setIsUrlFilterActive] = useState(false);

  // Tri/filtre actif via KPI cliquable
  const [activeKpiSort, setActiveKpiSort] = useState<KpiSortType>(null);

  // Lecture des query params URL pour pré-appliquer des filtres
  useEffect(() => {
    const margeParam = searchParams.get('marge');

    if (margeParam === 'negative') {
      setFilterMargeNegative(true);
      setIsUrlFilterActive(true);
    } else {
      setFilterMargeNegative(false);
      setIsUrlFilterActive(false);
    }
  }, [searchParams]);

  // Fonction pour effacer les filtres URL
  const clearUrlFilters = useCallback(() => {
    setSearchParams({});
    setFilterMargeNegative(false);
    setIsUrlFilterActive(false);
  }, [setSearchParams]);

  // Handler pour clic sur KPI (tri/filtre de la liste)
  const handleKpiClick = useCallback((sortType: KpiSortType) => {
    // Si on clique sur le même KPI déjà actif, on désactive
    if (activeKpiSort === sortType) {
      setActiveKpiSort(null);
    } else {
      setActiveKpiSort(sortType);
    }
    // Effacer aussi le filtre URL marge négative si on change de KPI
    if (sortType !== 'en_perte' && filterMargeNegative) {
      clearUrlFilters();
    }
  }, [activeKpiSort, filterMargeNegative, clearUrlFilters]);

  // Charger l'entreprise de l'utilisateur
  useEffect(() => {
    if (user) {
      loadEntreprise();
    }
  }, [user]);

  const loadEntreprise = async () => {
    try {
      const { data, error } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user?.id)
        .maybeSingle();

      if (error) throw error;
      if (data) setEntrepriseId(data.id);
    } catch (error: any) {
      console.error("Erreur chargement entreprise:", error);
    }
  };

  // Charger tous les chantiers avec stats d'engagement
  const { chantiers, loading } = useChantiersFiltresRentabilite(entrepriseId);

  // Filtrer et calculer KPIs selon le filtre actif
  const { filtered, kpis } = useChantiersFiltres(chantiers, filtreActif);

  // Compteurs pour les badges de chaque filtre
  const compteurs = useCompteursFiltres(chantiers);

  // Filtres visibles : masquer ceux à 0 résultats (sauf "engage" et "tous")
  const filtresVisibles = useMemo(() => {
    const toujoursVisibles: FiltreRentabilite[] = ['engage', 'tous'];
    return FILTRES_LIST.filter(f =>
      toujoursVisibles.includes(f) || (compteurs[f] ?? 0) > 0
    );
  }, [compteurs]);

  // Garde-fou : reset vers "engage" si le filtre actif n'est plus visible
  useEffect(() => {
    if (!filtresVisibles.includes(filtreActif)) {
      setFiltreActif('engage');
    }
  }, [filtresVisibles, filtreActif]);

  // Filtrer par recherche (en plus du filtre principal) + filtre marge négative URL + tri KPI
  const chantiersAffiches = useMemo(() => {
    let result = [...filtered];

    // Appliquer le filtre marge négative si actif (via URL ou KPI "En perte")
    if (filterMargeNegative || activeKpiSort === 'en_perte') {
      result = result.filter(c => {
        const margePct = c.metrics?.marge_finale_pct ?? 0;
        return margePct < 0;
      });
    }

    // Appliquer la recherche
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.nom_chantier.toLowerCase().includes(search) ||
          c.client.toLowerCase().includes(search) ||
          (c.adresse || "").toLowerCase().includes(search)
      );
    }

    // Appliquer le tri selon le KPI actif
    if (activeKpiSort && activeKpiSort !== 'chantiers' && activeKpiSort !== 'en_perte') {
      result.sort((a, b) => {
        switch (activeKpiSort) {
          case 'revenus':
            // Tri par budget HT (revenus) décroissant
            return b.budget_ht - a.budget_ht;
          case 'couts':
            // Tri par coûts totaux décroissants
            const coutsA = (a.metrics?.couts_fixes_engages || 0) + (a.metrics?.cout_main_oeuvre_reel || 0);
            const coutsB = (b.metrics?.couts_fixes_engages || 0) + (b.metrics?.cout_main_oeuvre_reel || 0);
            return coutsB - coutsA;
          case 'marge':
            // Tri par marge décroissante
            return (b.metrics?.marge_finale || 0) - (a.metrics?.marge_finale || 0);
          case 'rentabilite':
            // Tri par taux de rentabilité décroissant
            return (b.metrics?.marge_finale_pct || 0) - (a.metrics?.marge_finale_pct || 0);
          default:
            return 0;
        }
      });
    }

    return result;
  }, [filtered, searchTerm, filterMargeNegative, activeKpiSort]);

  return (
    <>
    <div className="space-y-8 animate-fade-up">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
          <TrendingUp className="h-9 w-9 text-primary" aria-hidden="true" />
          Rentabilité des Chantiers
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Vue d'ensemble de la rentabilité de tous vos chantiers
        </p>
      </div>

      {/* Bandeau filtre actif (si URL params) */}
      {isUrlFilterActive && (
        <div className="flex items-center justify-between gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-red-500" />
            <span className="text-sm font-medium text-red-700 dark:text-red-400">
              Filtre actif : Chantiers en perte (marge négative)
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearUrlFilters}
            className="h-8 px-2 text-red-700 dark:text-red-400 hover:bg-red-500/20"
          >
            <X className="h-4 w-4 mr-1" />
            Effacer le filtre
          </Button>
        </div>
      )}

      {/* Bandeau tri/filtre KPI actif (sauf si déjà bandeau URL) */}
      {activeKpiSort && !isUrlFilterActive && (
        <div className="flex items-center justify-between gap-3 p-3 bg-primary/10 border border-primary/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              {activeKpiSort === 'en_perte' && 'Filtre actif : Chantiers en perte uniquement'}
              {activeKpiSort === 'revenus' && 'Tri actif : Par revenus décroissants'}
              {activeKpiSort === 'couts' && 'Tri actif : Par coûts décroissants'}
              {activeKpiSort === 'marge' && 'Tri actif : Par marge décroissante'}
              {activeKpiSort === 'rentabilite' && 'Tri actif : Par rentabilité décroissante'}
              {activeKpiSort === 'chantiers' && 'Tri par défaut'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveKpiSort(null)}
            className="h-8 px-2 text-primary hover:bg-primary/20"
          >
            <X className="h-4 w-4 mr-1" />
            Réinitialiser
          </Button>
        </div>
      )}

      {/* Dropdown de filtres + Bouton export */}
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[220px] font-semibold h-12 justify-between" data-miro-target="rentabilite-filtres">
              <span className="flex items-center gap-2">
                <span>{FILTRES_CONFIG[filtreActif].icon}</span>
                <span>{FILTRES_CONFIG[filtreActif].label}</span>
                <span className="px-2 py-0.5 text-xs rounded-full bg-muted font-bold">
                  {compteurs[filtreActif] || 0}
                </span>
              </span>
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[220px]">
            {filtresVisibles.map((filtre) => (
              <DropdownMenuItem
                key={filtre}
                onClick={() => setFiltreActif(filtre)}
                className={`font-semibold cursor-pointer ${filtreActif === filtre ? 'bg-accent' : ''}`}
              >
                <span className="flex items-center gap-2 w-full">
                  <span>{FILTRES_CONFIG[filtre].icon}</span>
                  <span>{FILTRES_CONFIG[filtre].label}</span>
                  <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-muted font-bold">
                    {compteurs[filtre] || 0}
                  </span>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Bouton export PDF */}
        <div data-miro-target="rentabilite-export-pdf">
          <RentabiliteExportButton
            chantiers={chantiersAffiches}
            kpis={kpis}
            filtreActif={FILTRES_CONFIG[filtreActif].label}
            disabled={loading}
          />
        </div>
      </div>

      {/* KPIs dynamiques - cliquables pour trier/filtrer */}
      <KPIsSection
        kpis={kpis}
        filtreLabel={FILTRES_CONFIG[filtreActif].label}
        loading={loading}
        onKpiClick={handleKpiClick}
        activeKpi={activeKpiSort}
      />

      {/* Message explicatif pour le filtre "Engagé" */}
      {filtreActif === "engage" && (
        <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/30 rounded-xl">
          <span className="text-xl">💰</span>
          <div>
            <p className="font-semibold text-primary">Filtre "Avec dépenses"</p>
            <p className="text-sm text-muted-foreground">
              Affiche les chantiers avec dépenses réelles.
            </p>
          </div>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="relative w-full max-w-md">
        <Search
          className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          placeholder="Rechercher un chantier..."
          className="pl-12 h-12 text-base"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Rechercher un chantier"
        />
      </div>

      {/* Liste des chantiers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {chantiersAffiches.length} chantier(s) affiché(s)
            {searchTerm && ` pour "${searchTerm}"`}
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="card-premium animate-pulse">
                <CardContent className="p-6 h-24" />
              </Card>
            ))}
          </div>
        ) : chantiersAffiches.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-xl font-semibold text-muted-foreground">
                Aucun chantier trouvé
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm
                  ? "Essayez avec d'autres mots-clés"
                  : `Aucun chantier ne correspond au filtre "${FILTRES_CONFIG[filtreActif].label}"`}
              </p>
            </CardContent>
          </Card>
        ) : (
          chantiersAffiches.map((chantier) => {
            const etat = STATUTS_CONFIG[chantier.etat_chantier] || STATUTS_CONFIG["brouillon"];
            const hasMetrics = chantier.metrics !== null;
            const rentabilityBadge = getRentabilityBadge(
              chantier.metrics?.marge_finale_pct || 0
            );

            return (
              <Card
                key={chantier.id}
                className="card-premium hover-lift cursor-pointer transition-all"
                onClick={() => navigate(`/financial/${chantier.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <Badge className={`${etat.color} font-semibold px-3 py-1`}>
                          {etat.icon} {etat.label}
                        </Badge>
                        <div className="flex items-center gap-2">
                          {chantier.pictogramme && chantier.couleur ? (
                            <ChantierIcon
                              pictogramme={chantier.pictogramme}
                              couleur={chantier.couleur}
                              size="sm"
                              showBackground={true}
                            />
                          ) : (
                            <Building className="h-5 w-5 text-muted-foreground" />
                          )}
                          <h3 className="text-xl font-black">{chantier.nom_chantier}</h3>
                        </div>
                        {hasMetrics && (
                          <Badge
                            className={`font-bold px-3 py-1 border-2 ${rentabilityBadge.bgColor} ${rentabilityBadge.color}`}
                          >
                            {rentabilityBadge.emoji} {rentabilityBadge.label}
                          </Badge>
                        )}
                        {chantier.est_engage && filtreActif !== 'engage' && (
                          <Badge variant="outline" className="text-primary border-primary">
                            💰 Avec dépenses
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-white/40">
                        <MapPin className="h-4 w-4" />
                        <span className="text-sm">
                          {chantier.adresse || "Aucune adresse"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <p className="text-sm text-white/70 font-medium">
                          {chantier.client}
                        </p>
                        {(chantier.date_debut_prevue || chantier.date_fin_effective || chantier.date_fin_estimee) && (
                          <div className="flex items-center gap-1.5 text-xs text-white font-medium">
                            <Calendar className="h-3.5 w-3.5" />
                            <span className="flex items-center gap-1">
                              {/* Date de début */}
                              {chantier.date_debut_prevue
                                ? new Date(chantier.date_debut_prevue).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                                : '?'
                              }
                              {' → '}
                              {/* Si retard : afficher date initiale barrée + date effective en orange */}
                              {(chantier.jours_retard ?? 0) > 0 && chantier.dateFinRetard ? (
                                <>
                                  <span className="line-through text-white/60">
                                    {/* Fallback: utiliser date_fin_estimee si date_fin_initiale n'existe pas */}
                                    {new Date(chantier.date_fin_initiale || chantier.date_fin_estimee!).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  </span>
                                  <span className="mx-1 text-white">→</span>
                                  <span className="text-orange-400 font-semibold">
                                    {new Date(chantier.dateFinRetard).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  </span>
                                </>
                              ) : (
                                /* Sans retard : afficher date de fin normale */
                                (chantier.date_fin_effective || chantier.date_fin_estimee)
                                  ? new Date(chantier.date_fin_effective || chantier.date_fin_estimee!).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                                  : '?'
                              )}
                            </span>
                          </div>
                        )}
                        {/* Badge retard si applicable */}
                        {(chantier.jours_retard ?? 0) > 0 && chantier.etat_chantier !== 'termine' && chantier.etat_chantier !== 'annule' && (
                          <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30 text-xs">
                            +{Math.round(chantier.jours_retard!)}j retard
                          </Badge>
                        )}
                      </div>
                    </div>

                    {hasMetrics ? (
                      <div className="flex gap-6 sm:gap-10 items-center flex-wrap">
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-white/80 uppercase mb-1">
                            Revenus
                          </p>
                          <p className="text-xl font-black text-green-600">
                            {chantier.budget_ht.toLocaleString("fr-FR", {
                              minimumFractionDigits: 2,
                            })}{" "}
                            €
                          </p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-white/80 uppercase mb-1">
                            Coûts
                          </p>
                          <p className="text-xl font-black text-orange-600">
                            {(
                              (chantier.metrics?.couts_fixes_engages || 0) +
                              (chantier.metrics?.cout_main_oeuvre_reel || 0)
                            ).toLocaleString("fr-FR", { minimumFractionDigits: 2 })}{" "}
                            €
                          </p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-white/80 uppercase mb-1">
                            Marge
                          </p>
                          <p className="text-xl font-black text-emerald-600">
                            {(chantier.metrics?.marge_finale || 0).toLocaleString(
                              "fr-FR",
                              { minimumFractionDigits: 2 }
                            )}{" "}
                            €
                          </p>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <p className="text-xs text-white/80 uppercase mb-1">
                            Taux
                          </p>
                          <p
                            className={`text-2xl font-black ${
                              (chantier.metrics?.marge_finale_pct || 0) >= 10
                                ? "text-green-600"
                                : (chantier.metrics?.marge_finale_pct || 0) >= 0
                                ? "text-amber-600"
                                : "text-red-600"
                            }`}
                          >
                            {(chantier.metrics?.marge_finale_pct || 0).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <AlertCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Aucune donnée financière
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PANNEAU GRAPHIQUES FIXED - Doit être EN DEHORS du conteneur animé
          car le transform de animate-fade-up crée un nouveau contexte de positionnement
          qui casse le position:fixed du panneau
          ═══════════════════════════════════════════════════════════════════════ */}
      <GraphiquesPanel chantiers={chantiersAffiches} />

      {/* Annotations Miro Rentabilité */}
      <MiroAnnotations
        pageId="rentabilite"
        annotations={[
          {
            id: "rentabilite-graphiques",
            targetSelector: "[data-miro-target='rentabilite-graphiques-panel']",
            text: "Ouvrez ce panneau pour voir l'évolution de votre rentabilité en graphiques",
            position: "left",
            offsetX: -30,
            offsetY: 0,
          },
          {
            id: "rentabilite-filtres",
            targetSelector: "[data-miro-target='rentabilite-filtres']",
            text: "Filtrez vos chantiers par statut pour analyser la rentabilité par catégorie",
            position: "top",
            offsetX: 0,
            offsetY: -20,
          },
          {
            id: "rentabilite-export",
            targetSelector: "[data-miro-target='rentabilite-export-pdf']",
            text: "Génère un rapport PDF de rentabilité à envoyer à votre comptable ou votre banquier",
            position: "bottom",
            offsetX: 0,
            offsetY: 20,
          },
        ]}
      />
    </>
  );
};

export default Rentabilite;
